import { PurchaseDocListItem, PurchaseDocStatus, Money } from "types/InvoiceInbox";

/**
 * How the register speaks about a document.
 *
 * The rule this file encodes: STATUS and FLAGS are different things. A document
 * that is awaiting approval can also be a suspected duplicate, arrive with a
 * changed bank account, and be addressed to another group company — all at
 * once. Squeezing that into one column loses exactly the information the
 * reviewer needs, so the badge shows where the document is in the workflow and
 * the flags are shown next to it.
 */

export type Tone = "info" | "success" | "attention" | "warning" | "critical" | undefined;

export function getStatusTone(status: PurchaseDocStatus): Tone {
  switch (status) {
    case "new":
      return "info";
    case "needs_review":
      return "attention";
    case "awaiting_approval":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "critical";
    case "exported":
      return "success";
    default:
      return undefined;
  }
}

export function getStatusLabelKey(status: PurchaseDocStatus): string {
  return `invoice_inbox_status_${status}`;
}

/**
 * Statuses an accountant can actually type into the accounting program —
 * everything else is not bookkeeping work yet (new/needs_review/awaiting) or
 * never will be (rejected). Mirrors erp-backend's
 * `api/services/invoice-inbox/register-query.js` `BOOKKEEPABLE_STATUSES`;
 * keep the two in sync by hand, there is nothing to import across repos.
 */
export const BOOKKEEPABLE_STATUSES: PurchaseDocStatus[] = ["approved", "exported"];

export function isBookkeepable(status: PurchaseDocStatus): boolean {
  return BOOKKEEPABLE_STATUSES.includes(status);
}

/** Money arrives as a DECIMAL string from the driver. Never do arithmetic on it raw. */
export function toAmount(value: Money): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function formatAmount(value: Money, currency?: string): string {
  const amount = toAmount(value);
  if (amount === null) return "—";
  return `${amount.toFixed(2)} ${currency || "EUR"}`;
}

/** The invoice number as printed on it, series first: "LT 072"; "" when neither is known. */
export function invoiceNumberWithSeries(doc: Pick<PurchaseDocListItem, "invoiceSeries" | "invoiceNumber">): string {
  return [doc.invoiceSeries, doc.invoiceNumber].filter(Boolean).join(" ");
}

/** True for a document whose amounts are not in euro. */
export function isForeignCurrency(doc: { currency?: string | null }): boolean {
  return !!doc.currency && doc.currency.trim().toUpperCase() !== "EUR";
}

/**
 * "22.95 EUR" for a converted foreign-currency document, null for a EUR one
 * or one whose rate is not published yet.
 */
export function formatEurEquivalent(doc: {
  currency?: string | null;
  grossAmountEur?: Money;
}): string | null {
  if (!isForeignCurrency(doc)) return null;
  const eur = toAmount(doc.grossAmountEur ?? null);
  return eur === null ? null : `${eur.toFixed(2)} EUR`;
}


/**
 * The "paid" tick — a person's explicit confirmation (`markedPaidAt`), or a
 * payment the payment import (Finvalda sync, the only writer of `paidAt`)
 * has actually seen. Either is enough to show green; see PurchaseDocListItem
 * for why the two are kept apart.
 */
export function isMarkedPaid(doc: Pick<PurchaseDocListItem, "markedPaidAt" | "paidAt">): boolean {
  return Boolean(doc.markedPaidAt || doc.paidAt);
}

/** The tick was set by the system, not a person: the supplier's invoices
 * arrive already paid, so the document was marked on arrival. */
export function isMarkedPaidAutomatically(
  doc: Pick<PurchaseDocListItem, "markedPaidAt" | "markedPaidBy" | "paidByArrangement">
): boolean {
  return Boolean(doc.markedPaidAt && !doc.markedPaidBy && doc.paidByArrangement);
}

/** Today's calendar date in Lithuania as YYYY-MM-DD — a deadline is a date, not an instant. */
export function todayInVilnius(now: number = Date.now()): string {
  // en-CA formats as YYYY-MM-DD, which is what the comparison below needs.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Vilnius",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(now));
}

/**
 * True when the due DATE is before today's date in Lithuania. Compared as
 * calendar days on purpose: a date-only "2026-09-10" parsed by `new Date`
 * is UTC midnight, and `< Date.now()` would flag an invoice due TODAY as
 * overdue from 03:00 local time onward. A datetime string is reduced to its
 * Lithuanian calendar day first.
 */
export function isDueDatePast(dueDate: string | null | undefined, now: number = Date.now()): boolean {
  if (!dueDate) return false;
  const raw = String(dueDate);
  let due = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due) || raw.length > 10) {
    const parsed = Date.parse(raw);
    if (Number.isNaN(parsed)) return false;
    due = todayInVilnius(parsed);
  }
  return due < todayInVilnius(now);
}

/** Overdue is about the supplier's deadline, not about our queue. */
export function isOverdue(doc: PurchaseDocListItem): boolean {
  if (doc.status === "approved" || doc.status === "exported") return false;
  return isDueDatePast(doc.dueDate);
}

/**
 * Whether a refresh brought a document that has been read since the last look
 * — one that was still being read, or one that arrived already read between
 * two polls. Only a read document is linked to its supplier and month, so only
 * this can close a gap in the register's missing-invoices banner.
 */
export function hasNewlyReadDoc(
  previous: Array<Pick<PurchaseDocListItem, "id" | "extractionStatus">>,
  next: Array<Pick<PurchaseDocListItem, "id" | "extractionStatus">>
): boolean {
  const before = new Map(previous.map((doc) => [doc.id, doc.extractionStatus]));
  return next.some((doc) => {
    if (doc.extractionStatus === "pending") return false;
    const was = before.get(doc.id);
    return was === undefined || was === "pending";
  });
}
